<script setup lang="ts">
  import { watch } from 'vue'
  import { useRoute } from 'vue-router'
  import defaultImage from '@/assets/images/eurogold.webp'
  import { useProductUnitShow } from '@/models/product-unit'

  const route = useRoute()

  const { productUnit, loading, getData } = useProductUnitShow()
  getData(route.params.code as string, {
    signature: route.query.signature,
  })
  watch(productUnit, (newVal) => {
    if (newVal) {
      document.title = `${newVal.code} - ${__APP_TITLE__}`
    }
  })

  function handleImageError (event: Event) {
    const target = event.target as HTMLImageElement
    if (target) target.src = defaultImage
  }
</script>

<template>
  <div class="relative max-h-screen overflow-auto py-4 flex flex-col">
    <div class="shrink-0 mb-4">
      <app-button :to="{name: 'home'}">
        <fa-icon
          icon="i-far-chevron-left"
          class="text-xl" />
      </app-button>
    </div>

    <div class="flex-center min-h-0 flex-1">
      <div
        v-if="loading"
        class="flex flex-col items-center gap-4">
        <div class="size-12 rounded-full border-2 border-t-primary-400 border-primary-400/20 animate-spin"></div>
        <p class="animate-text-gradient text-sm tracking-widest uppercase font-light">
          Verifying
        </p>
      </div>

      <div
        v-else-if="productUnit"
        class="max-w-sm mx-auto space-y-4">
        <div class="relative aspect-square size-80 rounded-2xl overflow-hidden border border-primary/20 bg-primary-800/60 shadow-[inset_0_0_20px_rgba(0,0,0,0.25)]">
          <div class="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden">
            <div
              class="animate-border-beam"
              style="--size: 200; --duration: 4; --aspect-ratio: 1/1;">
            </div>
          </div>
          <div class="p-6 flex-center h-full">
            <div class="relative">
              <div class="absolute right-0 -top-10 z-20">
                <img
                  src="@/assets/images/logo-app.webp"
                  :alt="productUnit.product.name"
                  class="size-6 mx-auto object-contain relative z-10">
              </div>

              <img
                :src="productUnit.product.image_url ?? ''"
                :alt="productUnit.product.name"
                class="size-[80%] mx-auto object-contain relative z-10"
                @error="handleImageError">

              <div class="absolute right-5 bottom-0 z-20 text-green-500">
                <fa-icon
                  icon="i-fas-badge-check"
                  class="text-4xl" />
              </div>
            </div>
          </div>
        </div>

        <div class="text-center flex flex-col gap-2">
          <h1 class="text-2xl font-medium tracking-widest font-algreya uppercase animate-text-gradient">
            Authenticated
          </h1>
          <p class="text-white font-algreya-sans tracking-wider">
            This code has been successfully<br>verified as original
          </p>
        </div>

        <div class="relative rounded-2xl overflow-hidden border border-primary/20 bg-slate-800/10 backdrop-blur-sm">
          <div class="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden">
            <div
              class="animate-border-beam"
              style="--size: 200; --duration: 5; --aspect-ratio: 1/1; --delay: 1;"></div>
          </div>
          <div class="relative p-4">
            <table class="w-full">
              <tbody>
                <tr>
                  <td>Product</td>
                  <td>{{ productUnit.product.name }}</td>
                </tr>
                <tr>
                  <td>Serial No</td>
                  <td class="tracking-wide">
                    {{ productUnit.code }}
                  </td>
                </tr>
                <tr>
                  <td>Gold Rate</td>
                  <td>{{ productUnit.product.rate }}</td>
                </tr>
                <tr>
                  <td>Weight</td>
                  <td>{{ productUnit.product.weight }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div
        v-else
        class="text-center flex flex-col items-center gap-3">
        <div class="size-16 rounded-full bg-danger/10 flex-center text-danger">
          <fa-icon
            icon="i-fas-x"
            class="text-xl" />
        </div>
        <p class="text-gray-400 text-sm">
          Product not found or invalid code.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
@reference "@/assets/styles/main.css";

tr {
  @apply border-b border-primary/10 last:border-none;
}

td {
  @apply py-2 text-sm;
}

td:first-child {
  @apply text-gray-300 w-24 align-top;
}

td:last-child {
  @apply text-white font-semibold;
}

.flex-center {
  @apply flex items-center justify-center;
}
</style>
